import { useMutation, UseMutationResult, useQuery, UseQueryResult } from '@taxdown/zeno-sama-query';
import CheckoutService from 'Services/checkout';
import NotificationService, { AlertTypes } from 'Services/notification';
import { getErrorDetail } from 'Services/errors';
import i18n from 'Services/i18n';
import { useGetQuery } from 'Hooks/useGetQuery';
import { FRONT_PLANS, PLAN_FLEX_LITE, plansNameMapping } from 'Constants/plans';
import {
  CheckoutType,
  PlansTypePrimitives,
  ResponseArrayType,
  ResponseType,
  CustomerType,
  PlansType,
  QueryKeys,
} from 'Types/common';

const planSelectors = (e: ResponseArrayType<PlansTypePrimitives>): PlansType[] => {
  const suscribedPlanFull = e.data.find(
    plan => plansNameMapping(plan.item.name) === FRONT_PLANS.FULL
  );
  const suscribedPlanProPlus = e.data.find(
    plan => plansNameMapping(plan.item.name) === FRONT_PLANS.PRO_PLUS
  );
  const suscribedPlanFullPlus = e.data.find(
    plan => plansNameMapping(plan.item.name) === FRONT_PLANS.FULL_PLUS
  );

  const plans = e.data.map(item => {
    if (
      suscribedPlanFull?.item.subscribed &&
      plansNameMapping(item.item.name) !== FRONT_PLANS.PRO_PLUS &&
      plansNameMapping(item.item.name) !== FRONT_PLANS.FULL_PLUS
    ) {
      return {
        ...item.item,
        subscribedFull: suscribedPlanFull.item.subscribed,
      };
    }
    if (
      suscribedPlanProPlus?.item.subscribed &&
      plansNameMapping(item.item.name) !== FRONT_PLANS.FULL_PLUS
    ) {
      return {
        ...item.item,
        available: false,
        subscribed: suscribedPlanProPlus?.item.subscribed,
      };
    }
    if (suscribedPlanFullPlus?.item.subscribed) {
      return {
        ...item.item,
        available: false,
        subscribedFull: suscribedPlanFullPlus.item.subscribed,
      };
    }

    if (plansNameMapping(item.item.name) === FRONT_PLANS.PRO_PLUS && !item.item.subscribed) {
      return {
        ...item.item,
        available: true,
      };
    }

    return item.item;
  });

  const filterPlan = plans.filter(item => item.name !== FRONT_PLANS.BASIC);
  const plansToRender: PlansType[] = filterPlan.map(item => ({
    ...item,
    name: plansNameMapping(item.name),
  }));

  return plansToRender;
};

const checkoutSelectors = (e: ResponseType<CheckoutType>): CheckoutType => {
  const plans = e.data;
  window.location.assign(plans.item.sessionUrl);
  return plans.item;
};

const customerSelector = (data: ResponseType<CustomerType>): CustomerType => {
  const customer = data.data;

  return customer.item;
};

export const useFetchGetPlans = (): UseQueryResult<PlansType[]> =>
  useQuery([QueryKeys.FETCH_GET_PLANS], CheckoutService.getPlans, {
    select: data => planSelectors(data),
    refetchOnWindowFocus: false,
    cacheTime: 0,
  });

export const useFetchCheckout = (): UseMutationResult<unknown, unknown, unknown, unknown> =>
  useMutation([QueryKeys.CHEKOUT_SESSION], CheckoutService.checkoutSession, {
    onSuccess: data => checkoutSelectors(data),
    onError: error => NotificationService.sendNotification(getErrorDetail(error), AlertTypes.error),
  });

export const useFetchGetCustomers = (isAuthenticated: boolean): UseQueryResult<CustomerType> =>
  useQuery([QueryKeys.FETCH_GET_CUSTOMERS], CheckoutService.getCustomer, {
    select: data => customerSelector(data),
    onError: () => {
      NotificationService.sendNotification(i18n.t('common:Error.default'), AlertTypes.error);
    },
    refetchOnWindowFocus: false,
    refetchOnMount: true,
    enabled: isAuthenticated,
    cacheTime: 0,
  });

export const useFetchCheckoutPreviousYears = (): UseMutationResult<ResponseType<CheckoutType>> =>
  useMutation([QueryKeys.CHECKOUT_PREVIOUS_YEARS], CheckoutService.checkoutPreviousYears, {
    onSuccess: data => checkoutSelectors(data),
    onError: error => NotificationService.sendNotification(getErrorDetail(error), AlertTypes.error),
  });

export const useGetUserPlan = (): FRONT_PLANS => {
  const data = useGetQuery<ResponseType<CustomerType>>([QueryKeys.FETCH_GET_CUSTOMERS]);
  const customerInfo = data?.data?.item;
  const plan = customerInfo?.activePlan
    ? customerInfo.activePlan.name === PLAN_FLEX_LITE
      ? FRONT_PLANS.FLEX_LITE
      : plansNameMapping(customerInfo.activePlan.name)
    : FRONT_PLANS.BASIC;

  return plan;
};
